import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Download,
  Eye,
  Hourglass,
  IndianRupee,
  ListChecks,
  Mail,
  PhoneCall,
  Snowflake,
  UserPlus,
  XCircle,
} from 'lucide-react';
import API from '../../lib/api';
import AdminFilterBar from './AdminFilterBar';
import AdminStatCard from './AdminStatCard';

// One catalog drives the cards, the section grouping, table column labels,
// the empty-state copy, and the active filter. Add a new bucket here and
// it appears everywhere automatically — backend just needs the matching
// QUERY_BUILDERS entry.
const CATEGORIES = [
  // ── Pre-Sale Leaks ─────────────────────────────────────────────────────
  {
    key: 'contact_no_account',
    label: 'Contact, No Signup',
    section: 'Pre-Sale Leaks',
    icon: Mail,
    color: '#0EA5E9',
    blurb: 'Submitted contact form but never created an account',
    stageHeader: 'Last Contacted',
  },
  {
    key: 'no_service',
    label: 'Signup, No Service',
    section: 'Pre-Sale Leaks',
    icon: UserPlus,
    color: '#6C63FF',
    blurb: 'Account created but no service order placed',
    stageHeader: 'Signed Up',
  },
  {
    key: 'unanswered_calls',
    label: 'Pending Call Requests',
    section: 'Pre-Sale Leaks',
    icon: PhoneCall,
    color: '#F97316',
    blurb: 'Call requested, still awaiting our response',
    stageHeader: 'Requested',
  },
  // ── Payment Stalls ─────────────────────────────────────────────────────
  {
    key: 'step_one',
    label: 'Stuck Before Payment',
    section: 'Payment Stalls',
    icon: ListChecks,
    color: '#F59E0B',
    blurb: 'Order started but stuck on a step before payment',
    stageHeader: 'Last Activity',
  },
  {
    key: 'payment_pending',
    label: 'Payment Pending',
    section: 'Payment Stalls',
    icon: IndianRupee,
    color: '#E57373',
    blurb: 'On the payment step but payment not done',
    stageHeader: 'Order Placed',
    amountColumn: true,
  },
  {
    key: 'payment_failed',
    label: 'Payment Failed',
    section: 'Payment Stalls',
    icon: XCircle,
    color: '#DC2626',
    blurb: 'Payment attempt failed and customer never retried',
    stageHeader: 'Last Attempt',
    amountColumn: true,
  },
  // ── Post-Sale At-Risk ──────────────────────────────────────────────────
  {
    key: 'stuck_post_payment',
    label: 'Workflow Stalled (Paid)',
    section: 'Post-Sale At-Risk',
    icon: AlertTriangle,
    color: '#EA580C',
    blurb: 'Paid but workflow has not progressed — refund risk',
    stageHeader: 'Last Progress',
  },
  {
    key: 'cancelled_orders',
    label: 'Cancelled Orders',
    section: 'Post-Sale At-Risk',
    icon: XCircle,
    color: '#991B1B',
    blurb: 'Orders cancelled by user — winback candidates',
    stageHeader: 'Cancelled',
    amountColumn: true,
  },
  {
    key: 'dormant_customers',
    label: 'Dormant Customers',
    section: 'Post-Sale At-Risk',
    icon: Snowflake,
    color: '#64748B',
    blurb: 'Past customers with no active order — upsell candidates',
    stageHeader: 'Last Activity',
  },
];

const SECTIONS = ['Pre-Sale Leaks', 'Payment Stalls', 'Post-Sale At-Risk'];

const HOURS_OPTIONS = [
  { value: 24,    label: '> 24 hours' },
  { value: 48,    label: '> 48 hours' },
  { value: 72,    label: '> 72 hours' },
  { value: 168,   label: '> 7 days' },
  { value: 720,   label: '> 30 days' },
  { value: 2160,  label: '> 90 days' },
];

function formatStalled(hours) {
  if (hours == null) return '–';
  const h = Number(hours);
  if (Number.isNaN(h)) return '–';
  if (h >= 24 * 30) {
    return `${Math.floor(h / (24 * 30))}mo`;
  }
  const days = Math.floor(h / 24);
  const rem  = Math.floor(h % 24);
  if (days <= 0) return `${rem}h`;
  return `${days}d ${rem}h`;
}

function formatMoney(amount) {
  if (amount == null) return '–';
  return `₹${Number(amount).toLocaleString('en-IN')}`;
}

export default function StalledUsersPanel() {
  const navigate = useNavigate();
  const [category, setCategory] = useState('no_service');
  const [filters, setFilters] = useState({ search: '', hours: 24 });
  const [page, setPage] = useState(1);
  const [data, setData] = useState({
    users: [],
    total: 0,
    page: 1,
    pages: 1,
    counts: {},
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const active = CATEGORIES.find((c) => c.key === category) || CATEGORIES[0];

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {
        category,
        hours: filters.hours,
        page,
        limit: 20,
      };
      if (filters.search) params.search = filters.search;
      const { data: res } = await API.get('/admin/stalled-users', { params });
      setData({
        users:  res.users || [],
        total:  res.total || 0,
        page:   res.page  || 1,
        pages:  res.pages || 1,
        counts: res.counts || {},
      });
    } catch (err) {
      console.error(err);
      setError(
        err.response?.data?.detail || err.message || 'Failed to load stalled users'
      );
      setData((d) => ({ ...d, users: [], total: 0, pages: 1 }));
    }
    setLoading(false);
  }, [category, filters.hours, filters.search, page]);

  useEffect(() => {
    load();
  }, [load]);

  const handleFilter = (key, val) => {
    setFilters((f) => ({ ...f, [key]: val }));
    setPage(1);
  };
  const handleReset = () => {
    setFilters({ search: '', hours: 24 });
    setPage(1);
  };

  const handleCategory = (key) => {
    setCategory(key);
    setPage(1);
  };

  const [exporting, setExporting] = useState(false);
  const handleExport = async () => {
    setExporting(true);
    try {
      // Pull every matching row for the active category + hours threshold +
      // search. Server caps `limit` at 100, so page through.
      const baseParams = { category, hours: filters.hours };
      if (filters.search) baseParams.search = filters.search;

      const all = [];
      let p = 1;
      while (true) {
        const { data: res } = await API.get('/admin/stalled-users', {
          params: { ...baseParams, page: p, limit: 100 },
        });
        all.push(...(res.users || []));
        if (p >= (res.pages || 1) || (res.users || []).length === 0) break;
        p += 1;
      }

      const headers = [
        'User ID', 'Name', 'Email', 'Phone',
        active.stageHeader,
        'Stalled', 'Stalled (hours)',
        ...(active.amountColumn ? ['Amount Due (₹)'] : []),
        'Order ID', 'Category',
      ];

      // RFC 4180: quote on comma / quote / newline.
      const esc = (v) => {
        if (v == null) return '';
        const s = String(v);
        return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      };

      const rows = all.map((u) => {
        const base = [
          `#${u.id}`,
          u.name,
          u.email,
          u.phone,
          u.stage_at ? new Date(u.stage_at).toLocaleString('en-IN') : '',
          formatStalled(u.stalled_hours),
          u.stalled_hours ?? '',
        ];
        if (active.amountColumn) base.push(u.amount_due ?? '');
        base.push(u.order_id ? `#${u.order_id}` : '');
        base.push(active.label);
        return base.map(esc).join(',');
      });

      const csv = [headers.map(esc).join(','), ...rows].join('\r\n');
      // UTF-8 BOM so Excel renders Indian names / ₹ correctly.
      const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      const stamp = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `stalled-${category}-${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
      alert(err.response?.data?.detail || 'Failed to export stalled users');
    }
    setExporting(false);
  };

  return (
    <div>
      {/* Hero header */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#7F1D1D] via-[#C2410C] to-[#F97316] text-white px-6 py-6 mb-5">
        <div className="absolute -top-20 -right-12 w-60 h-60 bg-[#FBBF24]/30 rounded-full blur-3xl" />
        <div className="absolute -bottom-24 -left-12 w-60 h-60 bg-[#EF4444]/25 rounded-full blur-3xl" />
        <div className="relative flex items-end justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-white/15 backdrop-blur-sm border border-white/15 flex items-center justify-center">
              <Hourglass className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-heading font-bold leading-tight">
                Stalled Users
              </h1>
              <p className="text-sm text-white/85 font-body mt-0.5">
                Every funnel drop-off we can detect — re-engage to recover revenue
              </p>
            </div>
          </div>
          {(() => {
            const totalStalled = Object.values(data.counts || {}).reduce((s, n) => s + (n || 0), 0);
            return (
              <div className="flex items-center gap-3 sm:gap-5">
                <div>
                  <p className="text-[10px] font-body uppercase tracking-[0.18em] text-white/60">Total Stalled</p>
                  <p className="text-2xl font-heading font-bold mt-0.5">{totalStalled}</p>
                </div>
                <div className="h-9 w-px bg-white/15" />
                <div>
                  <p className="text-[10px] font-body uppercase tracking-[0.18em] text-white/60">Current Bucket</p>
                  <p className="text-2xl font-heading font-bold mt-0.5">{data.counts?.[category] ?? 0}</p>
                </div>
              </div>
            );
          })()}
        </div>
      </div>

      {/* Stat cards grouped by section */}
      <div className="space-y-5 mb-6">
        {SECTIONS.map((section) => {
          const cats = CATEGORIES.filter((c) => c.section === section);
          return (
            <div key={section}>
              <h2 className="text-xs font-heading font-semibold uppercase tracking-wider text-[#9CA3AF] mb-2">
                {section}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {cats.map((c) => {
                  const isActive = c.key === category;
                  return (
                    <button
                      key={c.key}
                      type="button"
                      onClick={() => handleCategory(c.key)}
                      className={`text-left rounded-2xl transition-all ${isActive
                        ? 'ring-2 ring-[#6C63FF]/40'
                        : 'hover:translate-y-[-1px]'
                        }`}
                    >
                      <AdminStatCard
                        icon={c.icon}
                        label={c.label}
                        value={data.counts?.[c.key] ?? 0}
                        color={c.color}
                        sub={isActive ? 'Showing below' : c.blurb}
                      />
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Filters */}
      <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
        <div>
          <h3 className="text-sm font-heading font-semibold text-[#1A1A2E]">
            {active.label}
          </h3>
          <p className="text-xs text-[#6B7280] font-body mt-0.5">{active.blurb}</p>
        </div>
      </div>
      <AdminFilterBar
        filters={filters}
        onFilterChange={handleFilter}
        onReset={handleReset}
      >
        <select
          value={filters.hours}
          onChange={(e) => handleFilter('hours', Number(e.target.value))}
          className="text-sm font-body border border-[#E8E6F8] rounded-lg px-3 py-2 bg-[#F5F4FB] text-[#1A1A2E] focus:outline-none focus:ring-1 focus:ring-[#6C63FF]/50"
        >
          {HOURS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              Stalled {o.label}
            </option>
          ))}
        </select>
        <button
          onClick={handleExport}
          disabled={exporting || data.total === 0}
          title={`Download ${active.label} as CSV`}
          className="flex items-center gap-1.5 text-xs font-body font-medium text-white bg-[#6C63FF] hover:bg-[#5B52CC] disabled:opacity-60 disabled:cursor-not-allowed px-3 py-2 rounded-lg transition-colors"
        >
          {exporting ? (
            <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <Download className="h-3.5 w-3.5" />
          )}
          {exporting ? 'Exporting…' : 'Export CSV'}
        </button>
      </AdminFilterBar>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-[#E8E6F8] shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-6 h-6 border-2 border-[#6C63FF] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : error ? (
          <div className="p-6 text-center">
            <p className="text-sm text-red-500 font-body">{error}</p>
            <button
              onClick={load}
              className="mt-2 text-xs text-[#6C63FF] underline font-body"
            >
              Retry
            </button>
          </div>
        ) : data.users.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-14 text-center">
            <Hourglass className="h-7 w-7 text-[#9CA3AF] mb-2" />
            <p className="text-sm font-body text-[#6B7280]">
              No users in this category right now.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm font-body">
              <thead>
                <tr className="bg-[#F5F4FB] border-b border-[#E8E6F8]">
                  {[
                    'ID',
                    'Name',
                    'Email',
                    'Phone',
                    active.stageHeader,
                    'Stalled Since',
                    active.amountColumn ? 'Amount Due' : (active.key === 'no_service' || active.key === 'contact_no_account' || active.key === 'dormant_customers' ? '' : 'Order'),
                    '',
                  ]
                    .filter(Boolean)
                    .map((h, i) => (
                      <th
                        key={`${h}-${i}`}
                        className="px-4 py-3 text-left text-xs font-heading font-semibold text-[#6B7280] whitespace-nowrap"
                      >
                        {h}
                      </th>
                    ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E8E6F8]">
                {data.users.map((u) => {
                  const hasAccount =
                    active.key !== 'contact_no_account';
                  return (
                    <tr
                      key={`${u.id}-${u.order_id || 'u'}`}
                      className="hover:bg-[#F5F4FB] transition-colors"
                    >
                      <td className="px-4 py-3 font-mono text-xs text-[#9CA3AF]">
                        #{u.id}
                      </td>
                      <td className="px-4 py-3 font-medium text-[#1A1A2E]">
                        {u.name || '–'}
                      </td>
                      <td className="px-4 py-3 text-[#6B7280]">{u.email}</td>
                      <td className="px-4 py-3 text-[#6B7280]">{u.phone || '–'}</td>
                      <td className="px-4 py-3 text-xs text-[#9CA3AF] whitespace-nowrap">
                        {u.stage_at
                          ? new Date(u.stage_at).toLocaleString('en-IN')
                          : '–'}
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-block text-[10px] px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 font-medium">
                          {formatStalled(u.stalled_hours)}
                        </span>
                      </td>
                      {active.amountColumn ? (
                        <td className="px-4 py-3 text-[#6B7280] whitespace-nowrap">
                          {formatMoney(u.amount_due)}
                        </td>
                      ) : active.key === 'no_service' || active.key === 'contact_no_account' || active.key === 'dormant_customers' ? null : (
                        <td className="px-4 py-3 text-[#6B7280] whitespace-nowrap">
                          {u.order_id ? `#${u.order_id}` : '–'}
                        </td>
                      )}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {u.phone && (
                            <a
                              href={`https://wa.me/${String(u.phone).replace(/\D/g, '')}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              title={`WhatsApp ${u.name || 'user'}`}
                              className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#25D366] hover:bg-[#1ebe57] text-white transition-colors"
                            >
                              <WhatsAppIcon className="h-3.5 w-3.5" />
                            </a>
                          )}
                          {u.email && (
                            <a
                              href={`mailto:${u.email}`}
                              onClick={(e) => e.stopPropagation()}
                              title={`Email ${u.name || 'lead'}`}
                              className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#6C63FF] hover:bg-[#5a52e0] text-white transition-colors"
                            >
                              <Mail className="h-3 w-3" />
                            </a>
                          )}
                          {hasAccount && (
                            <button
                              onClick={() =>
                                navigate(
                                  u.order_id
                                    ? `/admin/orders/${u.order_id}`
                                    : `/admin/users/${u.id}`
                                )
                              }
                              className="flex items-center gap-1 text-xs text-[#6C63FF] hover:underline font-medium"
                            >
                              <Eye className="h-3.5 w-3.5" />
                              {u.order_id ? 'Order' : 'User'}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {data.pages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-xs text-[#6B7280] font-body">
            Showing {(page - 1) * 20 + 1}–{Math.min(page * 20, data.total)} of {data.total}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="flex items-center gap-1 text-xs font-body px-3 py-1.5 border border-[#E8E6F8] rounded-lg hover:bg-[#F5F4FB] disabled:opacity-40"
            >
              <ChevronLeft className="h-3.5 w-3.5" /> Prev
            </button>
            <span className="text-xs font-body text-[#6B7280]">
              Page {page} / {data.pages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(data.pages, p + 1))}
              disabled={page === data.pages}
              className="flex items-center gap-1 text-xs font-body px-3 py-1.5 border border-[#E8E6F8] rounded-lg hover:bg-[#F5F4FB] disabled:opacity-40"
            >
              Next <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function WhatsAppIcon({ className = '' }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.149-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413" />
    </svg>
  );
}
